/**
 * Hand-off from this web app to the CatchUpper learner mobile app.
 *
 * Phones are not supported by the web app (App.tsx shows a blocking "device not supported" modal under 768px), yet
 * K12Net's mobile app can only open us in the phone's browser — it cannot launch our app itself. So a learner who
 * taps CatchUpper inside K12 used to land on a dead end. These helpers let the K12 landing screen send that learner
 * into the installed app instead (or to the store when it is not installed), and let the app's own K12 sign-in
 * come back through the same web callback K12Net already has registered.
 *
 * Pure functions with no React dependency — see `MobileAppHandoffUtilization.test.ts`.
 */

/** URL scheme the mobile app registers (Info.plist CFBundleURLTypes / AndroidManifest intent-filter). */
export const MOBILE_APP_SCHEME = "catchupper";

/** Android applicationId. Pinning it in the intent URL means no other app can receive what we hand over. */
export const MOBILE_APP_ANDROID_PACKAGE = "com.catchuppermobile";

export const MOBILE_APP_STORE_URL_IOS =
  "https://apps.apple.com/tr/app/catchupper-%C3%B6%C4%9Frenci/id6751711641";

export const MOBILE_APP_STORE_URL_ANDROID = `https://play.google.com/store/apps/details?id=${MOBILE_APP_ANDROID_PACKAGE}`;

/** App path that starts a K12 sign-in inside the app. */
export const MOBILE_APP_K12_ENTRY_PATH = "login/k12";

/** App path that receives K12Net's authorization code for a sign-in the app started. */
export const MOBILE_APP_K12_CALLBACK_PATH = "login/k12-callback";

/**
 * App path that signs in with the SEBIT token from the landing URL (`token` + `clientid`, SEBIT's own spelling).
 *
 * Unlike K12, the app cannot START a SEBIT sign-in — SEBIT is the identity provider and only its campus tile mints a
 * token — so the token itself is what gets handed over. The app posts it to the same backend endpoint this web app
 * uses (`/management/integrations/sebit/login`).
 */
export const MOBILE_APP_SEBIT_PATH = "login/sebit";

/** App path that receives an external-client hand-off's session pair (`accessToken` + `refreshToken`). */
export const MOBILE_APP_HANDOFF_PATH = "login/handoff";

/**
 * Whether phones arriving through SEBIT or an external-client hand-off are sent to the mobile app.
 *
 * OFF unless the build sets `REACT_APP_MOBILE_APP_SSO_HANDOFF=true`. The app learned these two sign-ins later than
 * K12; until that version is live in BOTH stores, an installed older app would open with nothing to sign in with —
 * the same blank-tab trap the K12 redirect fell into when it shipped ahead of the app. So the switch is flipped in
 * the hosting environment once the release is out, not in code. K12 is not behind it: the app has handled K12 since
 * 1.4.
 */
export const isMobileAppSsoHandoffEnabled = (
  flag: string | null | undefined = process.env.REACT_APP_MOBILE_APP_SSO_HANDOFF
): boolean => flag === "true";

/**
 * Login routes that render their own phone "continue in the app" card, so App.tsx's phone "device not supported"
 * modal must not cover them. SEBIT and external-client only count while {@link isMobileAppSsoHandoffEnabled}: with
 * the switch off they behave exactly as before, modal included.
 */
export const isMobileAppHandoffRoute = (
  pathname: string | null | undefined,
  ssoHandoffEnabled: boolean
): boolean => {
  const path = String(pathname ?? "");
  if (path.startsWith("/login/with-k12")) {
    return true;
  }
  return (
    ssoHandoffEnabled &&
    (path.startsWith("/login/with-sebit") ||
      path.startsWith("/login/with-external-client"))
  );
};

/**
 * Prefix the mobile app puts on the OAuth `state` of a K12 sign-in it starts.
 *
 * K12Net only knows one learner callback (`<app web>/login/with-k12`), and both the web flow and the app's flow come
 * back to it. The prefix is how this page tells them apart: a web-started flow has its `state` in this tab's
 * sessionStorage, an app-started one only in the app — so for those the page does not check `state` at all and
 * simply hands the callback to the (Android) app, which does.
 *
 * Contract with CatchUpperMobile: the app sends `m.` + base64url of 32 secure-random bytes (45 characters, alphabet
 * `A-Za-z0-9_-`) — `src/services/K12LoginService.ts`, generated natively in `K12AuthSession.m` / `K12AuthModule.kt`.
 * {@link isMobileAppK12State} accepts exactly that shape (and some slack); change both sides together.
 */
export const MOBILE_APP_K12_STATE_PREFIX = "m.";

export type PhonePlatform = "ios" | "android";

/**
 * Which phone OS this browser runs on, or `null` for anything that is not a phone.
 *
 * Tablets are deliberately `null`: the web app supports them, so an iPad or Android tablet keeps the web flow.
 * iPadOS 13+ reports a desktop Mac user agent and so falls out naturally; Android tablets omit `Mobile`.
 */
export const detectPhonePlatform = (
  userAgent: string | null | undefined
): PhonePlatform | null => {
  const ua = String(userAgent ?? "");
  if (/iPhone|iPod/i.test(ua)) {
    return "ios";
  }
  if (/Android/i.test(ua) && /Mobile/i.test(ua)) {
    return "android";
  }
  return null;
};

/**
 * Whether a K12 callback should be forwarded to the Android app: any Android device, phone or tablet.
 *
 * Only Android gets a forward (a package-pinned intent). iOS receives its callback inside the app's auth session and
 * is never sent a code from this page — see `LoginWithK12Screen`.
 */
export const isAndroidDevice = (userAgent: string | null | undefined): boolean =>
  /Android/i.test(String(userAgent ?? ""));

/**
 * Which store to offer in the "device not supported" modal, or `null` for no store link.
 *
 * Phones by user agent; beyond that only touch tablets the app actually runs on — Android tablets (no `Mobile` in the
 * UA) and iPads, which on iPadOS 13+ report a desktop Mac user agent and are told apart by their touch points. A
 * narrow window on a touch-screen Windows laptop or a Chromebook gets no link.
 */
export const detectStorePlatform = (
  userAgent: string | null | undefined,
  maxTouchPoints: number | null | undefined
): PhonePlatform | null => {
  const phonePlatform = detectPhonePlatform(userAgent);
  if (phonePlatform) {
    return phonePlatform;
  }
  if (!maxTouchPoints || maxTouchPoints <= 1) {
    return null;
  }
  const ua = String(userAgent ?? "");
  if (/Android/i.test(ua)) {
    return "android";
  }
  if (/Macintosh/i.test(ua) && !/CrOS/i.test(ua)) {
    return "ios";
  }
  return null;
};

/** Whether a returned `state` belongs to a sign-in the mobile app started. */
export const isMobileAppK12State = (state: string | null | undefined): boolean =>
  typeof state === "string" &&
  state.startsWith(MOBILE_APP_K12_STATE_PREFIX) &&
  /^[A-Za-z0-9._-]{8,256}$/.test(state);

/**
 * A URL that opens `path` in the mobile app.
 *
 * - iOS: the custom scheme (`catchupper://path?query`).
 * - Android: an `intent://` URL pinned to our package. Unlike a bare custom scheme, Android resolves it ONLY to
 *   `com.catchuppermobile`, so an authorization code handed over this way cannot be picked up by another app that
 *   registered the same scheme. With `fallbackUrl`, Chrome opens that URL instead when the app is not installed.
 */
export const buildMobileAppUrl = (
  platform: PhonePlatform,
  path: string,
  params?: Record<string, string | null | undefined>,
  fallbackUrl?: string
): string => {
  const query = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      query.set(key, value);
    }
  });
  const queryString = query.toString();
  const pathWithQuery = queryString ? `${path}?${queryString}` : path;

  if (platform === "ios") {
    return `${MOBILE_APP_SCHEME}://${pathWithQuery}`;
  }
  const fallback = fallbackUrl
    ? `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};`
    : "";
  return `intent://${pathWithQuery}#Intent;scheme=${MOBILE_APP_SCHEME};package=${MOBILE_APP_ANDROID_PACKAGE};${fallback}end`;
};

export const storeUrlFor = (platform: PhonePlatform): string =>
  platform === "ios" ? MOBILE_APP_STORE_URL_IOS : MOBILE_APP_STORE_URL_ANDROID;

/** What a SEBIT / external-client landing screen shows a phone instead of signing in on the web. */
export type SsoMobileAppHandoff = {
  platform: PhonePlatform;
  /** `null` on iOS: there is no channel there that only our app can receive (see {@link buildSsoMobileAppHandoff}). */
  appUrl: string | null;
};

/**
 * The phone hand-off for a credential-carrying SSO landing (SEBIT's token, or an external-client session pair).
 *
 * - Android: a package-pinned `intent://` URL carrying the credential, falling back to the Play Store when the app is
 *   not installed. Pinned to `com.catchuppermobile`, so no other app can be handed it.
 * - iOS: NOTHING is forwarded. A custom scheme can be claimed by any app (iOS does not reserve schemes, RFC 8252
 *   §8.1), so a credential must never ride on one — the same rule the K12 callback follows. On iOS the installed app
 *   receives these URLs as Universal Links before the page loads; a page that does load means the app is missing
 *   (or the link was opened in the browser on purpose), and the card offers the store.
 */
export const buildSsoMobileAppHandoff = (
  platform: PhonePlatform,
  path: string,
  params: Record<string, string | null | undefined>
): SsoMobileAppHandoff => ({
  platform,
  appUrl:
    platform === "android"
      ? buildMobileAppUrl(platform, path, params, storeUrlFor(platform))
      : null,
});
